import React, { useState, useEffect } from 'react';
import styles from './SendInvoiceModal.module.css';
import Button from '../ui/button';
import Dropdown from '../ui/dropdown';
import Input from '../ui/input';
import { getCompactAnnotationsSummary, getPredefinedInvoiceText, sendInvoice, getCompanyBillingEmail } from '../../services/apiService';
import { showErrorToast, showSuccessToast } from '../../utils/notifications';
import SambaFilePickerModal from './SambaFilePickerModal';

/**
 * Modal para enviar una factura con anotación, asunto, idioma e información
 * Props:
 * - isOpen: boolean (si el modal está visible)
 * - onClose: función para cerrar el modal
 * - invoiceId: number (ID de la factura)
 * - expedienteId: number (ID del expediente)
 * - documentType: string (tipo de documento: Factura, Pre-factura, F. Rectificativa)
 * - onSuccess: función para ejecutar tras el envío exitoso
 */
export default function SendInvoiceModal({ 
  isOpen, 
  onClose,
  invoiceId,
  expedienteId,
  documentType,
  onSuccess 
}) {
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [subject, setSubject] = useState('Envío factura');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [information, setInformation] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSambaFilePicker, setShowSambaFilePicker] = useState(false);
  const [toEmail, setToEmail] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [bccEmail, setBccEmail] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedAnnotation(null);
      setSubject('Envío factura');
      setSelectedTemplate(null);
      setInformation('');
      setAttachments([]);
      setToEmail('');
      setCcEmail('');
      setBccEmail('');
    }
  }, [isOpen]);

  // Cargar anotaciones cuando se abre el modal
  useEffect(() => {
    const loadAnnotations = async () => {
      if (isOpen && expedienteId) {
        try {
          setLoading(true);
          const annotationsSummary = await getCompactAnnotationsSummary(expedienteId);
          
          // Filtrar solo anotaciones con gmailMessageId (no vacío)
          const formattedAnnotations = annotationsSummary
            .filter(annotation => annotation.gmailMessageId && annotation.gmailMessageId.trim() !== '')
            .map(annotation => ({
              value: annotation.id,
              label: `${annotation.asunto} - ${annotation.contenido.substring(0, 50)}${annotation.contenido.length > 50 ? '...' : ''}`,
              data: annotation
            }));
          
          setAnnotations(formattedAnnotations);
        } catch (error) {
          console.error('Error loading annotations:', error);
          showErrorToast('Error al cargar las anotaciones');
        } finally {
          setLoading(false);
        }
      }
    };

    loadAnnotations();
  }, [isOpen, expedienteId]);

  // Cargar opciones de plantillas predefinidas
  useEffect(() => {
    const loadTemplates = async () => {
      if (isOpen) {
        try {
          const predefinedTexts = await getPredefinedInvoiceText();
          const formattedTemplates = predefinedTexts.map(text => ({
            value: text.id,
            label: text.id,
            data: text
          }));
          setTemplateOptions(formattedTemplates);
        } catch (error) {
          console.error('Error loading templates:', error);
          showErrorToast('Error al cargar las plantillas');
        }
      }
    };

    loadTemplates();
  }, [isOpen]);

  // Cargar correo de facturación de la compañía para pre-rellenar CC
  useEffect(() => {
    const loadBillingEmail = async () => {
      if (isOpen && expedienteId) {
        try {
          const email = await getCompanyBillingEmail(expedienteId);
          if (email && email.trim()) {
            setCcEmail(email);
          }
        } catch (error) {
          console.error('Error loading billing email:', error);
          // No mostrar error al usuario, simplemente dejar el campo vacío
        }
      }
    };

    loadBillingEmail();
  }, [isOpen, expedienteId]);

  // Cargar emails to, cc y bcc cuando se selecciona una anotación
  useEffect(() => {
    if (selectedAnnotation && selectedAnnotation.data) {
      setToEmail(selectedAnnotation.data.toAddresses || '');
      
      // Para CC: combinar el email de facturación (si existe) con los CC de la anotación
      const annotationCc = selectedAnnotation.data.ccAddresses || '';
      const billingCc = ccEmail; // Email de facturación ya cargado
      
      // Combinar y eliminar duplicados
      const allCc = [billingCc, annotationCc]
        .filter(email => email && email.trim())
        .join(', ');
      
      setCcEmail(allCc);
      setBccEmail(selectedAnnotation.data.bccAddresses || '');
    } else {
      setToEmail('');
      // No limpiar CC porque puede tener el email de facturación
      setBccEmail('');
    }
  }, [selectedAnnotation, ccEmail]);

  // Actualizar información cuando se selecciona una plantilla
  useEffect(() => {
    if (selectedTemplate) {
      setInformation(selectedTemplate.data.body);
    }
  }, [selectedTemplate]);

  const handleSend = async () => {
    try {
      setSending(true);
      // Extraer idioma del template seleccionado (ESP/CAT)
      const language = selectedTemplate?.value?.startsWith('ESP') ? 'ESP' : 'CAT';
      
      const sendData = {
        annotationId: selectedAnnotation?.value,
        gmailMessageId: selectedAnnotation?.data?.gmailMessageId, // ID del mensaje de Gmail
        subject,
        language,
        information,
        attachments,
        to: toEmail || '',
        cc: ccEmail || '',
        bcc: bccEmail || ''
      };
      
      await sendInvoice(invoiceId, sendData);
      showSuccessToast('Factura enviada correctamente');
      handleReset();
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error sending invoice:', error);
      const errorMessage = error?.response?.data?.detail || error?.response?.data?.message || 'Error al enviar la factura';
      showErrorToast(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleCancel = () => {
    handleReset();
    onClose();
  };

  const handleReset = () => {
    setSelectedAnnotation(null);
    setSubject('Envío factura');
    setSelectedTemplate(null);
    setInformation('');
    setAttachments([]);
    setToEmail('');
    setCcEmail('');
    setBccEmail('');
  };

  const handleSambaFilesSelect = (fileObjects) => {
    // fileObjects es un array de objetos con {path, name, size}
    // Extraer solo los paths como strings
    const newPaths = (fileObjects || []).map(file => {
      if (typeof file === 'string') return file;
      if (file && typeof file === 'object' && file.path) return file.path;
      return null;
    }).filter(path => path); // Remover nulls
    
    // Añadir solo los que no existen ya (evitar duplicados)
    setAttachments(prevFiles => {
      const existingPaths = prevFiles.filter(f => typeof f === 'string');
      const filesToAdd = newPaths.filter(path => !existingPaths.includes(path));
      return [...prevFiles, ...filesToAdd];
    });
  };

  const removeAttachment = (index) => {
    setAttachments(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div>
              <h3 className={styles.title}>Enviar Factura</h3>
              <p className={styles.subtitle}>Configura los detalles para enviar la factura</p>
            </div>
          </div>
          <button className={styles.closeButton} onClick={handleCancel}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          <div className={styles.formContainer}>
            <div className={styles.row}>
              <Dropdown
                label="Anotación"
                options={annotations}
                selected={selectedAnnotation}
                onSelect={setSelectedAnnotation}
                placeholder={loading ? "Cargando anotaciones..." : "Selecciona una anotación"}
                disabled={loading}
                style={{ flex: 1 }}
              />
              </div>

              <div className={styles.row}>
                <Input
                  label="Asunto"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Asunto del envío"
                  style={{ flex: 1 }}
                />
              </div>

              <div className={styles.row}>
                <Input
                  label="Para (To)"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="Destinatario principal"
                  style={{ flex: 1 }}
                />
              </div>

              <div className={styles.row}>
                <Input
                  label="CC"
                  value={ccEmail}
                  onChange={(e) => setCcEmail(e.target.value)}
                  placeholder="Copia (correos separados por coma)"
                  type="email"
                  style={{ flex: 1 }}
                />
              </div>

              <div className={styles.row}>
                <Input
                  label="CCO (BCC)"
                  value={bccEmail}
                  onChange={(e) => setBccEmail(e.target.value)}
                  placeholder="Copia oculta (correos separados por coma)"
                  type="email"
                  style={{ flex: 1 }}
                />
              </div>

              <div className={styles.row}>
                <Dropdown
                  label="Plantilla de texto"
                  options={templateOptions}
                  selected={selectedTemplate}
                  onSelect={setSelectedTemplate}
                  placeholder="Selecciona una plantilla"
                  style={{ flex: 1 }}
                />
              </div>

              <div className={styles.row}>
                <div className={styles.textareaContainer}>
                  <label className={styles.textareaLabel}>Información</label>
                  <textarea
                    className={styles.textarea}
                    value={information}
                    onChange={(e) => setInformation(e.target.value)}
                    placeholder="Información adicional"
                    rows={10}
                    spellCheck="true"
                  />
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.attachmentContainer}>
                  <label className={styles.attachmentLabel}>Archivos adjuntos</label>
                  <div className={styles.fileButtonsContainer}>
                    <Button
                      onClick={() => setShowSambaFilePicker(true)}
                      variant="primary"
                    >
                      Seleccionar archivos
                    </Button>
                  </div>
                  
                  {attachments.length > 0 && (
                    <div className={styles.attachmentList}>
                      {attachments.map((file, index) => {
                        const fileName = typeof file === 'string' ? file.split('/').pop() : file.name;
                        return (
                          <div key={index} className={styles.attachmentItem}>
                            <span className={styles.fileName}>{fileName}</span>
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                              className={styles.removeButton}
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={sending}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={sending || !subject.trim()}
          >
            {sending ? 'Enviando...' : 'Enviar Factura'}
          </Button>
        </div>
      </div>

      {/* Modal de selección de archivos SAMBA */}
      <SambaFilePickerModal
        isOpen={showSambaFilePicker}
        onClose={() => setShowSambaFilePicker(false)}
        onFilesSelect={handleSambaFilesSelect}
        expId={expedienteId}
        expedienteYear={Math.floor(parseInt(expedienteId.toString().replace(/\s+/g, '')) / 10000) + 2000}
        multiple={true}
      />
    </div>
  );
}
